// Slice 48 (V2.1) — Staff screen.
//
// Three live sections, top to bottom:
//   1. Active staff — table with role/credentialCount/lastLoginAt + admin
//      controls (PATCH role, DELETE staff). Guards (last-admin, self-delete)
//      surface as toast errors on the response.
//   2. Pending invites — table of open/claimed/revoked invites; admin can
//      revoke an open one. The freshly-created invite link is shown in a
//      modal so the admin can copy it out of band (no SMTP).
//   3. My passkeys — list of the signed-in staff's own credentials; add
//      via WebAuthn or remove. Add-credential uses
//      `@simplewebauthn/browser` for the create-ceremony.
//
// Role guards: admin sees all controls; staff (read-only) sees the lists
// but the mutation buttons are hidden. The backend re-checks via
// `requireAdminRole` so this is UI-affordance only.

import { startRegistration } from "@simplewebauthn/browser";
import { useCallback, useEffect, useState } from "react";
import {
  InvitesApi,
  type OwnCredential,
  PasskeyApi,
  RecoveryApi,
  type RecoveryCodesIssued,
  type RecoveryStats,
  StaffApi,
  type StaffInvite,
  type StaffInviteCreated,
  type StaffRole,
  type StaffRow,
} from "../../api/client";
import { useShell } from "../../shell/HqShellContext";
import { HqBadge, HqButton, HqCard, HqInput, HqModal, HqSelect, Icons } from "../../ui";
import { MonoId } from "../licenses/MonoId";

export function StaffScreen() {
  const { role, refreshTick, bumpRefresh, toast } = useShell();
  const isAdmin = role === "admin";
  const [staff, setStaff] = useState<StaffRow[]>([]);
  const [invites, setInvites] = useState<StaffInvite[]>([]);
  const [credentials, setCredentials] = useState<OwnCredential[]>([]);
  const [recovery, setRecovery] = useState<RecoveryStats>({ remaining: 0, generatedAt: null });
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [createdInvite, setCreatedInvite] = useState<StaffInviteCreated | null>(null);
  const [issuedCodes, setIssuedCodes] = useState<RecoveryCodesIssued | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    const [sRes, iRes, cRes, rRes] = await Promise.all([
      StaffApi.list(),
      isAdmin ? InvitesApi.list() : Promise.resolve({ ok: true as const, data: [] }),
      PasskeyApi.list(),
      RecoveryApi.stats(),
    ]);
    setLoading(false);
    if (sRes.ok) setStaff(sRes.data);
    else toast(sRes.error.message, "danger");
    if (iRes.ok) setInvites(iRes.data);
    else if ("error" in iRes) toast(iRes.error.message, "danger");
    if (cRes.ok) setCredentials(cRes.data);
    else toast(cRes.error.message, "danger");
    if (rRes.ok) setRecovery(rRes.data);
    else toast(rRes.error.message, "danger");
  }, [isAdmin, toast]);

  useEffect(() => {
    void refreshTick;
    void reload();
  }, [reload, refreshTick]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Header
        staffCount={staff.length}
        openInviteCount={invites.filter((i) => i.status === "open").length}
        onInvite={isAdmin ? () => setInviteOpen(true) : null}
      />

      <StaffTable
        staff={staff}
        loading={loading}
        isAdmin={isAdmin}
        onRoleChange={async (id, nextRole) => {
          const res = await StaffApi.updateRole(id, nextRole);
          if (!res.ok) {
            toast(res.error.message, "danger");
            return;
          }
          toast(`Role updated to ${nextRole}.`, "success");
          bumpRefresh();
        }}
        onRemove={async (id, username) => {
          if (!confirm(`Remove ${username}? Sessions revoked + credentials wiped.`)) return;
          const res = await StaffApi.remove(id);
          if (!res.ok) {
            toast(res.error.message, "danger");
            return;
          }
          toast(`Removed ${username}.`, "success");
          bumpRefresh();
        }}
      />

      {isAdmin && (
        <InvitesTable
          invites={invites}
          onRevoke={async (id) => {
            const res = await InvitesApi.revoke(id);
            if (!res.ok) {
              toast(res.error.message, "danger");
              return;
            }
            toast("Invite revoked.", "success");
            bumpRefresh();
          }}
        />
      )}

      <BackupCodesCard
        stats={recovery}
        onRegenerate={async () => {
          if (
            recovery.generatedAt !== null &&
            !confirm("Regenerate backup codes? Existing codes will be invalidated immediately.")
          ) {
            return;
          }
          const res = await RecoveryApi.regenerate();
          if (!res.ok) {
            toast(res.error.message, "danger");
            return;
          }
          setIssuedCodes(res.data);
          setRecovery({ remaining: res.data.codes.length, generatedAt: res.data.generatedAt });
          toast("Backup codes regenerated — copy them now.", "success");
          bumpRefresh();
        }}
      />

      <BackupCodesModal issued={issuedCodes} onClose={() => setIssuedCodes(null)} toast={toast} />

      <CredentialsCard
        credentials={credentials}
        onAdd={async () => {
          const begin = await PasskeyApi.beginAdd();
          if (!begin.ok) {
            toast(begin.error.message, "danger");
            return;
          }
          try {
            const att = await startRegistration({
              optionsJSON: begin.data as Parameters<typeof startRegistration>[0]["optionsJSON"],
            });
            const deviceName =
              prompt("Name this device (laptop, phone, yubikey, …):", "this device") ?? undefined;
            const finish = await PasskeyApi.finishAdd(att, deviceName);
            if (!finish.ok) {
              toast(finish.error.message, "danger");
              return;
            }
            toast(`Passkey added (${finish.data.deviceName ?? "device"}).`, "success");
            bumpRefresh();
          } catch (e) {
            toast(e instanceof Error ? e.message : "Passkey registration cancelled.", "warn");
          }
        }}
        onRemove={async (id) => {
          if (!confirm("Remove this passkey? Other passkeys + password still work.")) return;
          const res = await PasskeyApi.remove(id);
          if (!res.ok) {
            toast(res.error.message, "danger");
            return;
          }
          toast("Passkey removed.", "success");
          bumpRefresh();
        }}
      />

      <InviteModal
        open={inviteOpen}
        onClose={() => {
          setInviteOpen(false);
          setCreatedInvite(null);
        }}
        onCreated={(invite) => {
          setCreatedInvite(invite);
          bumpRefresh();
        }}
        created={createdInvite}
        toast={toast}
      />
    </div>
  );
}

function Header({
  staffCount,
  openInviteCount,
  onInvite,
}: {
  staffCount: number;
  openInviteCount: number;
  onInvite: (() => void) | null;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <Icons.Shield width={20} height={20} sw={1.7} style={{ color: "var(--hq-accent)" }} />
      <div style={{ display: "flex", flexDirection: "column" }}>
        <h1
          style={{
            margin: 0,
            fontSize: 24,
            fontWeight: 600,
            letterSpacing: "-.02em",
            color: "var(--hq-text)",
            lineHeight: 1.2,
          }}
        >
          Staff & passkeys
        </h1>
        <span className="hq-mono" style={{ fontSize: 11, color: "var(--hq-text-dim)" }}>
          {staffCount} staff · {openInviteCount} pending invite{openInviteCount === 1 ? "" : "s"} ·{" "}
          V2.1
        </span>
      </div>
      <span style={{ flex: 1 }} />
      {onInvite && (
        <HqButton onClick={onInvite}>
          <Icons.Plus width={13} height={13} sw={2.2} />
          Invite staff
        </HqButton>
      )}
    </div>
  );
}

function StaffTable({
  staff,
  loading,
  isAdmin,
  onRoleChange,
  onRemove,
}: {
  staff: StaffRow[];
  loading: boolean;
  isAdmin: boolean;
  onRoleChange: (id: string, role: StaffRole) => Promise<void>;
  onRemove: (id: string, username: string) => Promise<void>;
}) {
  return (
    <HqCard padding={false}>
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid var(--hq-border)",
          display: "flex",
          alignItems: "center",
        }}
      >
        <span
          className="hq-mono"
          style={{
            fontSize: 10.5,
            letterSpacing: ".1em",
            textTransform: "uppercase",
            color: "var(--hq-text-dim)",
          }}
        >
          Active staff
        </span>
      </div>
      {staff.length === 0 ? (
        <div
          style={{ padding: 24, textAlign: "center", color: "var(--hq-text-dim)", fontSize: 12.5 }}
        >
          {loading ? "Loading…" : "No staff."}
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--hq-border)" }}>
              <Th>Username</Th>
              <Th>Role</Th>
              <Th>Passkeys</Th>
              <Th>Password</Th>
              <Th>Last login</Th>
              {isAdmin && <Th> </Th>}
            </tr>
          </thead>
          <tbody>
            {staff.map((s) => (
              <StaffRowView
                key={s.id}
                row={s}
                isAdmin={isAdmin}
                onRoleChange={onRoleChange}
                onRemove={onRemove}
              />
            ))}
          </tbody>
        </table>
      )}
    </HqCard>
  );
}

function StaffRowView({
  row,
  isAdmin,
  onRoleChange,
  onRemove,
}: {
  row: StaffRow;
  isAdmin: boolean;
  onRoleChange: (id: string, role: StaffRole) => Promise<void>;
  onRemove: (id: string, username: string) => Promise<void>;
}) {
  return (
    <tr style={{ borderBottom: "1px solid var(--hq-border)" }}>
      <td style={{ padding: "10px 14px" }}>
        <span className="hq-mono" style={{ fontSize: 12, color: "var(--hq-text)" }}>
          {row.username}
        </span>
      </td>
      <td style={{ padding: "10px 14px" }}>
        {isAdmin ? (
          <HqSelect<StaffRole>
            options={[
              { value: "admin", label: "admin" },
              { value: "staff", label: "staff (read-only)" },
            ]}
            value={row.role}
            onChange={(v) => void onRoleChange(row.id, v)}
            size="sm"
          />
        ) : (
          <HqBadge tone={row.role === "admin" ? "accent" : "neutral"} size="sm">
            {row.role}
          </HqBadge>
        )}
      </td>
      <td style={{ padding: "10px 14px" }}>
        <span style={{ fontSize: 12, color: "var(--hq-text-muted)" }}>{row.credentialCount}</span>
      </td>
      <td style={{ padding: "10px 14px" }}>
        <HqBadge tone={row.hasPassword ? "neutral" : "warn"} size="sm">
          {row.hasPassword ? "set" : "passkey-only"}
        </HqBadge>
      </td>
      <td style={{ padding: "10px 14px" }}>
        <span className="hq-mono" style={{ fontSize: 11, color: "var(--hq-text-dim)" }}>
          {row.lastLoginAt ? fmtTs(row.lastLoginAt) : <em>never</em>}
        </span>
      </td>
      {isAdmin && (
        <td style={{ padding: "10px 14px", textAlign: "right" }}>
          <HqButton variant="ghost" size="sm" onClick={() => void onRemove(row.id, row.username)}>
            <Icons.Revoke width={13} height={13} sw={1.8} />
            Remove
          </HqButton>
        </td>
      )}
    </tr>
  );
}

function InvitesTable({
  invites,
  onRevoke,
}: {
  invites: StaffInvite[];
  onRevoke: (id: string) => Promise<void>;
}) {
  if (invites.length === 0) return null;
  return (
    <HqCard padding={false}>
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid var(--hq-border)",
          display: "flex",
          alignItems: "center",
        }}
      >
        <span
          className="hq-mono"
          style={{
            fontSize: 10.5,
            letterSpacing: ".1em",
            textTransform: "uppercase",
            color: "var(--hq-text-dim)",
          }}
        >
          Invites
        </span>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ borderBottom: "1px solid var(--hq-border)" }}>
            <Th>Username</Th>
            <Th>Role</Th>
            <Th>Invited by</Th>
            <Th>Status</Th>
            <Th>Expires</Th>
            <Th> </Th>
          </tr>
        </thead>
        <tbody>
          {invites.map((i) => (
            <tr key={i.id} style={{ borderBottom: "1px solid var(--hq-border)" }}>
              <td style={{ padding: "10px 14px" }}>
                <span className="hq-mono" style={{ fontSize: 12, color: "var(--hq-text)" }}>
                  {i.username}
                </span>
                {i.email && (
                  <div style={{ fontSize: 10.5, color: "var(--hq-text-dim)" }}>{i.email}</div>
                )}
              </td>
              <td style={{ padding: "10px 14px" }}>
                <HqBadge tone={i.role === "admin" ? "accent" : "neutral"} size="sm">
                  {i.role}
                </HqBadge>
              </td>
              <td style={{ padding: "10px 14px" }}>
                <span className="hq-mono" style={{ fontSize: 11, color: "var(--hq-text-muted)" }}>
                  {i.invitedBy}
                </span>
              </td>
              <td style={{ padding: "10px 14px" }}>
                <HqBadge tone={inviteStatusTone(i.status)} size="sm">
                  {i.status}
                </HqBadge>
              </td>
              <td style={{ padding: "10px 14px" }}>
                <span className="hq-mono" style={{ fontSize: 11, color: "var(--hq-text-dim)" }}>
                  {fmtTs(i.expiresAt)}
                </span>
              </td>
              <td style={{ padding: "10px 14px", textAlign: "right" }}>
                {i.status === "open" && (
                  <HqButton variant="ghost" size="sm" onClick={() => void onRevoke(i.id)}>
                    Revoke
                  </HqButton>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </HqCard>
  );
}

function inviteStatusTone(s: StaffInvite["status"]): "accent" | "success" | "warn" | "neutral" {
  switch (s) {
    case "open":
      return "accent";
    case "claimed":
      return "success";
    case "revoked":
      return "warn";
    case "expired":
      return "neutral";
  }
}

function CredentialsCard({
  credentials,
  onAdd,
  onRemove,
}: {
  credentials: OwnCredential[];
  onAdd: () => Promise<void>;
  onRemove: (id: string) => Promise<void>;
}) {
  return (
    <HqCard padding={false}>
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid var(--hq-border)",
          display: "flex",
          alignItems: "center",
        }}
      >
        <span
          className="hq-mono"
          style={{
            fontSize: 10.5,
            letterSpacing: ".1em",
            textTransform: "uppercase",
            color: "var(--hq-text-dim)",
            flex: 1,
          }}
        >
          My passkeys
        </span>
        <HqButton size="sm" onClick={() => void onAdd()}>
          <Icons.Plus width={13} height={13} sw={2.2} />
          Add passkey
        </HqButton>
      </div>
      {credentials.length === 0 ? (
        <div
          style={{ padding: 20, textAlign: "center", color: "var(--hq-text-dim)", fontSize: 12.5 }}
        >
          No passkeys yet. Click{" "}
          <strong style={{ color: "var(--hq-text-muted)" }}>Add passkey</strong> to register your
          current device.
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--hq-border)" }}>
              <Th>Device</Th>
              <Th>Transports</Th>
              <Th>Added</Th>
              <Th>Last used</Th>
              <Th> </Th>
            </tr>
          </thead>
          <tbody>
            {credentials.map((c) => (
              <tr key={c.id} style={{ borderBottom: "1px solid var(--hq-border)" }}>
                <td style={{ padding: "10px 14px" }}>
                  <span style={{ fontSize: 12, color: "var(--hq-text)" }}>
                    {c.deviceName ?? <em style={{ color: "var(--hq-text-dim)" }}>unnamed</em>}
                  </span>
                </td>
                <td style={{ padding: "10px 14px" }}>
                  <span
                    className="hq-mono"
                    style={{ fontSize: 10.5, color: "var(--hq-text-muted)" }}
                  >
                    {c.transports.length > 0 ? c.transports.join(", ") : "—"}
                  </span>
                </td>
                <td style={{ padding: "10px 14px" }}>
                  <span className="hq-mono" style={{ fontSize: 11, color: "var(--hq-text-dim)" }}>
                    {fmtTs(c.createdAt)}
                  </span>
                </td>
                <td style={{ padding: "10px 14px" }}>
                  <span className="hq-mono" style={{ fontSize: 11, color: "var(--hq-text-dim)" }}>
                    {c.lastUsedAt ? fmtTs(c.lastUsedAt) : <em>never</em>}
                  </span>
                </td>
                <td style={{ padding: "10px 14px", textAlign: "right" }}>
                  <HqButton variant="ghost" size="sm" onClick={() => void onRemove(c.id)}>
                    <Icons.Revoke width={13} height={13} sw={1.8} />
                    Remove
                  </HqButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </HqCard>
  );
}

function InviteModal({
  open,
  onClose,
  onCreated,
  created,
  toast,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (invite: StaffInviteCreated) => void;
  created: StaffInviteCreated | null;
  toast: (msg: string, tone?: "success" | "warn" | "danger" | "info") => void;
}) {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<StaffRole>("staff");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async () => {
    setSubmitting(true);
    const res = await InvitesApi.create({
      username: username.trim().toLowerCase(),
      role,
      ...(email.trim() ? { email: email.trim() } : {}),
    });
    setSubmitting(false);
    if (!res.ok) {
      toast(res.error.message, "danger");
      return;
    }
    onCreated(res.data);
    toast("Invite created — copy the link.", "success");
  };

  return (
    <HqModal open={open} onClose={onClose} title={created ? "Invite created" : "Invite staff"}>
      {created ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <p style={{ margin: 0, fontSize: 13, color: "var(--hq-text-muted)" }}>
            Send this link to{" "}
            <strong style={{ color: "var(--hq-text)" }}>{created.username}</strong>. Expires{" "}
            {fmtTs(created.expiresAt)}.
          </p>
          <div
            style={{
              padding: 12,
              background: "var(--hq-bg)",
              border: "1px solid var(--hq-border)",
              borderRadius: "var(--hq-radius-md)",
              fontFamily: "var(--hq-font-mono)",
              fontSize: 11.5,
              color: "var(--hq-text)",
              wordBreak: "break-all",
            }}
          >
            {created.url}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <HqButton
              onClick={() => {
                void navigator.clipboard.writeText(created.url);
                toast("Copied to clipboard.", "success");
              }}
            >
              <Icons.Copy width={13} height={13} sw={1.8} />
              Copy link
            </HqButton>
            <HqButton variant="secondary" onClick={onClose}>
              Done
            </HqButton>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <FieldLabel label="Username">
            <HqInput
              placeholder="ada"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              mono
              autoFocus
            />
          </FieldLabel>
          <FieldLabel label="Email (optional, for your own records)">
            <HqInput
              placeholder="ada@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </FieldLabel>
          <FieldLabel label="Role">
            <HqSelect<StaffRole>
              options={[
                { value: "admin", label: "admin (full mutations)" },
                { value: "staff", label: "staff (read-only)" },
              ]}
              value={role}
              onChange={(v) => setRole(v)}
            />
          </FieldLabel>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <HqButton variant="secondary" onClick={onClose}>
              Cancel
            </HqButton>
            <HqButton onClick={() => void onSubmit()} disabled={submitting || !username.trim()}>
              {submitting ? "Creating…" : "Create invite"}
            </HqButton>
          </div>
        </div>
      )}
    </HqModal>
  );
}

function FieldLabel({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 12, color: "var(--hq-text-dim)" }}>{label}</span>
      {children}
    </label>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <td
      style={{
        padding: "10px 14px",
        color: "var(--hq-text-dim)",
        fontFamily: "var(--hq-font-mono)",
        fontSize: 10.5,
        letterSpacing: ".1em",
        textTransform: "uppercase",
        textAlign: "left",
      }}
    >
      {children}
    </td>
  );
}

function fmtTs(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

function BackupCodesCard({
  stats,
  onRegenerate,
}: {
  stats: RecoveryStats;
  onRegenerate: () => Promise<void>;
}) {
  const issued = stats.generatedAt != null;
  return (
    <HqCard padding={false}>
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid var(--hq-border)",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <span
          className="hq-mono"
          style={{
            fontSize: 10.5,
            letterSpacing: ".1em",
            textTransform: "uppercase",
            color: "var(--hq-text-dim)",
            flex: 1,
          }}
        >
          Backup codes
        </span>
        <HqButton
          size="sm"
          variant={issued ? "secondary" : "primary"}
          onClick={() => void onRegenerate()}
        >
          {issued ? "Regenerate" : "Generate codes"}
        </HqButton>
      </div>
      <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
          <span className="hq-mono" style={{ fontSize: 18, color: "var(--hq-text)" }}>
            {stats.remaining}/8
          </span>
          <span style={{ fontSize: 12, color: "var(--hq-text-dim)" }}>codes left</span>
          {stats.generatedAt && (
            <span
              className="hq-mono"
              style={{ fontSize: 11, color: "var(--hq-text-dim)", marginLeft: "auto" }}
            >
              issued {fmtTs(stats.generatedAt)}
            </span>
          )}
        </div>
        <p style={{ margin: 0, fontSize: 12, color: "var(--hq-text-muted)", lineHeight: 1.5 }}>
          Single-use recovery codes let you set a new password and sign back in if you lose all your
          passkeys. They are shown <strong>only once</strong> at generation time — keep them in a
          password manager or a sealed envelope.
        </p>
      </div>
    </HqCard>
  );
}

function BackupCodesModal({
  issued,
  onClose,
  toast,
}: {
  issued: RecoveryCodesIssued | null;
  onClose: () => void;
  toast: (msg: string, tone?: "success" | "warn" | "danger" | "info") => void;
}) {
  const allText = issued ? issued.codes.join("\n") : "";
  return (
    <HqModal open={issued != null} onClose={onClose} title="Backup codes — shown only once">
      {issued && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <p style={{ margin: 0, fontSize: 13, color: "var(--hq-text-muted)" }}>
            Each code can be used <strong>once</strong> to reset your password and sign in. Store
            them somewhere safe before you close this dialog.
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 8,
              padding: 12,
              background: "var(--hq-bg)",
              border: "1px solid var(--hq-border)",
              borderRadius: "var(--hq-radius-md)",
              fontFamily: "var(--hq-font-mono)",
              fontSize: 14,
              color: "var(--hq-text)",
            }}
          >
            {issued.codes.map((c) => (
              <div key={c} style={{ padding: "4px 6px", letterSpacing: ".06em" }}>
                {c}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <HqButton
              variant="secondary"
              onClick={() => {
                void navigator.clipboard.writeText(allText);
                toast("All codes copied to clipboard.", "success");
              }}
            >
              <Icons.Copy width={13} height={13} sw={1.8} />
              Copy all
            </HqButton>
            <HqButton onClick={onClose}>I have saved them</HqButton>
          </div>
        </div>
      )}
    </HqModal>
  );
}

// Touch unused export so module-level imports don't drift.
void MonoId;
